
import { UserFieldDataTypeEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/user-field-data-type.enum';
import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core'; // Añadir OnInit
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { ToggleOptionComponent } from 'app/shared/components/form-ui/toggle-option/toggle-option.component';
import { CompanyFieldCatalogService } from '../company-form-config/company-form-config.service';
import { UserFieldEntityTypeEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/user-field-entity-type.enum';
import { IAPIItemResult } from 'app/common/interfaces/pagination-result.interface';
import UserFieldCatalogAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/fields-catalog/user-field-catalog/user-field-catalog.entity';
import { UpdateFieldPayload } from '../company-form-config/interfaces/field-catalog.interface';
import { take } from 'rxjs';

const USER_FIELD_DATATYPE_LIST = Object.values(UserFieldDataTypeEnum).map(value => ({
  label: value.charAt(0).toUpperCase() + value.slice(1),
  value: value
}));


@Component({
  selector: 'app-user-fields-catalog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatTabsModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    ToggleOptionComponent
  ],
  templateUrl: './user-fields-catalog.component.html',
  styleUrl: './user-fields-catalog.component.scss'
})
export class UserFieldsCatalogComponent implements OnInit {

  form!: FormGroup;
  entities: UserFieldEntityTypeEnum[] = Object.values(UserFieldEntityTypeEnum);
  selectedEntityIndex: number = 0;

  formConfigs: Record<UserFieldEntityTypeEnum, FormGroup> = {} as Record<UserFieldEntityTypeEnum, FormGroup>;

  readonly entityLabels: Record<UserFieldEntityTypeEnum, string> = {
    [UserFieldEntityTypeEnum.BUSINESS_PARTNER]: 'Socios de Negocio',
    [UserFieldEntityTypeEnum.BP_DISTRIBUTION_CHAIN]: 'Cadena de Distribución de socio',
    [UserFieldEntityTypeEnum.MATERIAL]: 'Materiales',
    [UserFieldEntityTypeEnum.MATERIAL_DISTRIBUTION_CHAIN]: 'Cadena de Distribución de material',
    [UserFieldEntityTypeEnum.DOCUMENT_CLASS]: 'Clases de Documento',
    [UserFieldEntityTypeEnum.SUB_ROUTE]: 'Sub-rutas',
    [UserFieldEntityTypeEnum.SALES_OFFICE]: 'Oficinas de Venta',
    [UserFieldEntityTypeEnum.PLANTS]: 'Plantas',
    [UserFieldEntityTypeEnum.WAREHOUSE]: 'Almacenes',
    [UserFieldEntityTypeEnum.DISTRIBUTION_CHANNEL]: 'Canales de Distribución',
    [UserFieldEntityTypeEnum.DIVISION]: 'Divisiones',
    [UserFieldEntityTypeEnum.DISTRIBUTION_CHAIN]: 'Cadenas de Distribución',
    [UserFieldEntityTypeEnum.CREDIT_APPLICATION]: 'Credito'
  };

  readonly dataTypeList = USER_FIELD_DATATYPE_LIST;

  constructor(
    private readonly companyFieldCatalogService: CompanyFieldCatalogService,
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<UserFieldsCatalogComponent>,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: {
      companyId: string,
    }
  ) { }

  ngOnInit(): void {
    this.entities.forEach(entity => {
      this.formConfigs[entity] = this.createEntityForm(entity);
    });

    this.loadAllConfigs();
  }

  private createEntityForm(entityType: UserFieldEntityTypeEnum): FormGroup {
    return this.fb.group<any>({
      _id: [null],
      entityType: [entityType, [Validators.required]],
      statusId: [0, [Validators.required]],
      companyId: [this.data.companyId, [Validators.required]],
      fields: this.fb.array([] as FormGroup[])
    });
  }

  private createFieldGroup(field?): FormGroup {
    return this.fb.group({
      name: [field?.name || '', [Validators.required]],
      labels: this.fb.group({
        master: [field?.labels?.master || '', [Validators.required]],
        report: [field?.labels?.report || '', [Validators.required]],
        mobile: [field?.labels?.mobile || '', [Validators.required]],
      }),
      statusId: [field?.statusId === 0 ? false : true || false],
      required: [field?.required || false],
      dataType: [field?.dataType || UserFieldDataTypeEnum.STRING, [Validators.required]],
    });
  }

  getFieldsArray(entityType: UserFieldEntityTypeEnum): FormArray {
    return this.formConfigs[entityType].get('fields') as FormArray;
  }

  getFieldControls(entityType: UserFieldEntityTypeEnum): FormGroup[] {
    return this.getFieldsArray(entityType).controls as FormGroup[];
  }

  getEntityLabel(entity: UserFieldEntityTypeEnum): string {
    return this.entityLabels[entity] ?? entity;
  }

  loadAllConfigs() {
    this.entities.forEach(entityType => {
      this.companyFieldCatalogService.getFieldsMaster(this.data.companyId, entityType)
      .pipe(take(1))
      .subscribe({
        next: (res: IAPIItemResult<UserFieldCatalogAttributes>) => {
          const response = res.data;

          const entityForm = this.formConfigs[entityType];
          const fieldsArray = this.getFieldsArray(entityType);

          fieldsArray.clear();

          entityForm.patchValue({
            _id: response._id || null,
            statusId: response.statusId === 1 ? true : false,
          });

          if (response.fields && response.fields.length > 0) {
            response.fields.forEach((field) => {
              fieldsArray.push(this.createFieldGroup(field));
            });
          }
        },
        error: (err) => {
          if (err.status !== 404) {
            this.snackBar.open(`Error al cargar ${this.getEntityLabel(entityType)}: ${err.message}`, 'Cerrar', { duration: 5000 });
          }
        }
      });
    });
  }

  addNewField(entityType: UserFieldEntityTypeEnum): void {
    const fieldsArray = this.getFieldsArray(entityType);
    fieldsArray.push(this.createFieldGroup());
  }

  mapStatusId(payload) {
    return {
      ...payload,
      statusId: payload.statusId ? 1 : 0,
      fields: payload.fields?.map((field) => ({
        ...field,
        statusId: field.statusId ? 1 : 0
      }))
    };
  }

  saveConfig(entityType: UserFieldEntityTypeEnum): void {
    const form = this.formConfigs[entityType];
    if (form.valid) {
      if (form.value._id) {
        const { _id, entityType, ...rest } = form.value; 

        const payload = this.mapStatusId(rest);

        this.companyFieldCatalogService.saveFieldsMaster(payload as UpdateFieldPayload, 'PATCH', form.value._id).subscribe({
          next: () => {
            this.snackBar.open('Configuración actualizada exitosamente!', 'Cerrar', { duration: 3000 });
          },
          error: (err) => {
            this.snackBar.open(`Error al actualizar: ${err.message}`, 'Cerrar', { duration: 5000 });
          }
        });
      } else {
        const { _id, ...payload } = form.value; 
        
        this.companyFieldCatalogService.saveFieldsMaster(payload as UpdateFieldPayload, 'POST').subscribe({
          next: () => {
            this.snackBar.open('Configuración guardada exitosamente!', 'Cerrar', { duration: 3000 });
          },
          error: (err) => {
            this.snackBar.open(`Error al guardar: ${err.message}`, 'Cerrar', { duration: 5000 });
          }
        });
      }
    } else {
      this.snackBar.open('Revise los campos requeridos.', 'Cerrar', { duration: 3000 });
      form.markAllAsTouched();
    }
  }

}